import React from "react";
import {
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { IUser } from "./types";
import { useSnackbar } from "../../contexts/SnackbarProvider";
import { useFormik } from "formik";
import * as Yup from "yup";
import useCreateData from "../../hooks/useCreateData";
import useUpdateData from "../../hooks/useUpdateData";
import { IControl } from "../controls/types";
import DialogButtons from "../../Components/DialogButtons";
import { useTranslation } from "react-i18next";

interface IUserFormProps {
  user: IUser;
  controls: IControl[];
}

const UserForm = (props: IUserFormProps) => {
  const { user, controls } = props;
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
  const navigate = useNavigate();
  const showMessage = useSnackbar();
  const { t } = useTranslation();

  const handleCancel = () => {
    showMessage({
      text: user.username
        ? t("Editing user USER cancelled", { user })
        : t("Creating new user cancelled"),
      severity: "info",
    });
    navigate("/users");
  };

  const createHandler = useCreateData();
  const updateHandler = useUpdateData();
  const formik = useFormik({
    initialValues: {
      username: user.username,
      email: user.email,
      is_operator: user.is_operator,
      checked: user.control_ids.map((id) => id.toString()),
    },
    validationSchema: Yup.object({
      username: Yup.string().required(t("Required")),
      email: Yup.string().required(t("Required")),
    }),
    onSubmit: async (values) => {
      setIsSubmitting(true);
      const allValues = { ...user, ...values };
      if (user.id === -1) {
        createHandler(`user "${allValues.username}"`, "/users", allValues);
      } else {
        updateHandler(
          `user "${allValues.username}"`,
          `/users/${user.id}`,
          allValues
        );
      }
      navigate("/users");
    },
  });

  return (
    <form onSubmit={formik.handleSubmit} data-cy="user-form">
      <TextField
        fullWidth
        autoFocus
        margin="normal"
        id="username"
        label={t("Username")}
        type="text"
        disabled={isSubmitting}
        error={formik.touched.username && Boolean(formik.errors.username)}
        helperText={formik.touched.username && formik.errors.username}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        value={formik.values.username}
      ></TextField>
      <FormControlLabel
        control={<Checkbox checked={formik.values.is_operator} />}
        name={"is_operator"}
        label={t("is operator")}
        onChange={formik.handleChange}
      />
      <TextField
        fullWidth
        autoFocus
        margin="normal"
        id="email"
        label={t("E-Mail")}
        type="text"
        disabled={isSubmitting}
        error={formik.touched.email && Boolean(formik.errors.email)}
        helperText={formik.touched.email && formik.errors.email}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        value={formik.values.email}
      ></TextField>
      {!user.is_admin && (
        <FormGroup>
          {controls
            .sort(function (a: IControl, b: IControl) {
              return a.name.localeCompare(b.name);
            })
            .map((control) => (
              <FormControlLabel
                key={control.id}
                control={
                  <Checkbox
                    checked={formik.values.checked.includes(
                      control.id.toString()
                    )}
                  />
                }
                name={"checked"}
                label={control.name}
                value={control.id}
                onChange={formik.handleChange}
              />
            ))}
        </FormGroup>
      )}
      <DialogButtons
        isSubmitting={isSubmitting}
        submitButtonLabel="Save"
        resetCallback={handleCancel}
      />
    </form>
  );
};

export default UserForm;
